import React from 'react'
import clsx from 'clsx'
import { ACCOUNT_MENU_SELECT_OWNER_ID_LABEL } from '@constants'
import { MkAccountMenuProps } from './types'
import { useAccountMenu } from './useAccountMenu'
import { MkAccountMenuWidget } from './MkAccountMenuWidget'
import { MkAccountMenuHeader } from './MkAccountMenuHeader'
import { MkAccountMenuItems } from './MkAccountMenuItems'
import './MkAccountMenu.scss'

/**
 * MkAccountMenu component renders an account menu with a widget, header, and menu items.
 *
 * @param {MkAccountMenuProps} props - The properties for the MkAccountMenu component.
 * @param {string} props.className - Additional class names for the component.
 * @param {string} props.kind - The kind of account menu.
 * @param {string} props.resourceKey - The resource key for fetching menu data.
 * @param {MenuItemConfig} props.menuItemConfig - Configuration for the menu items.
 *
 * @returns {JSX.Element} The rendered MkAccountMenu component.
 */
export const MkAccountMenu: React.FC<MkAccountMenuProps> = ({ className, kind, resourceKey, menuItemConfig, onCloseMenu, onMenuToggled }) => {
  const {
    widgetButtonConfigs,
    isMenuOpen,
    headerTitle,
    headerSubtitle,
    available,
    contractDetails,
    menuItems,
  }: ReturnType<typeof useAccountMenu> = useAccountMenu({
    kind,
    resourceKey,
    menuItemConfig,
    onMenuToggled: onMenuToggled || (() => {}),
    onCloseMenu,
  })

  return (
    <div
      className={clsx(
        'account-menu',
        {
          'account-menu--open': isMenuOpen === true,
        },
        className,
      )}
      data-testid="mkAccountMenu">
      <MkAccountMenuWidget
        className="account-menu__widget"
        buttonConfigs={widgetButtonConfigs}
      />
      <section className={clsx('account-menu__dropdown', { hidden: isMenuOpen === false })}>
        <MkAccountMenuHeader
          className="account-menu__dropdown__header"
          title={headerTitle}
          subtitle={headerSubtitle}
          selectOwnerIdLabel={ACCOUNT_MENU_SELECT_OWNER_ID_LABEL}
          contractDetails={contractDetails}
        />
        <div className="account-menu__dropdown__menu-wrapper">
          <MkAccountMenuItems
            className="account-menu__dropdown__menu"
            items={menuItems}
          />
        </div>
      </section>
    </div>
  )
}
